import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, Tooltip } from '@material-ui/core'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import OpalPresentationTable from '../../../DenaliAnalysis/tables/presentation'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import useStyleVariant from '../../../QueryBlueprint/hooks/useStyleVariant'
import Icon from '../../../../atoms/Icon'
import SmartComponentDisplayName from '../../SmartComponentDisplayName'
import useActivitySummary from './useActivitySummary'
import useActivitySummaryState from './useActivitySummaryState'
import ActivitySummarySettingsDialog from './ActivitySummarySettingsDialog'
import DrillDownDialog from './DrillDownDialog'
import { mergeConfig, QUICK_DATE_OPTIONS } from './constants'

const NAME_STYLES = {
  valuation: {},
  derived: { fontWeight: 400, fontStyle: 'italic', paddingLeft: '20px' },
  activity: { fontWeight: 400, paddingLeft: '20px' },
  performance: {}
}

const VALUE_STYLES = {
  valuation: {},
  derived: { fontWeight: 400, fontStyle: 'italic' },
  activity: { fontWeight: 400 },
  performance: {}
}

function buildColumns (formatter, onDrillClick, quickDateLabel, formats) {
  return [
    {
      id: 'name',
      header: '',
      accessorKey: 'name',
      size: 250,
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original
        const style = { ...(NAME_STYLES[row.type] ?? {}) }
        if (row.drillable && onDrillClick) {
          style.cursor = 'pointer'
          style.textDecoration = 'underline'
          style.textDecorationColor = 'var(--color-neutral-400)'
          style.textUnderlineOffset = '2px'
          return (
            <button
              type='button'
              style={{
                ...style,
                background: 'none',
                border: 0,
                padding: 0,
                margin: 0,
                font: 'inherit',
                color: 'inherit',
                textAlign: 'left'
              }}
              onClick={() => onDrillClick(row)}
            >
              {row.name}
            </button>
          )
        }
        return <span style={style}>{row.name}</span>
      }
    },
    {
      id: 'value',
      header: quickDateLabel || 'Amount',
      accessorKey: 'value',
      size: 160,
      align: 'right',
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original
        const formatKey = row.format === 'performance' ? formats.performance : formats.value
        const style = {
          ...(VALUE_STYLES[row.type] ?? {}),
          textAlign: 'right',
          width: '100%',
          display: 'inline-block'
        }
        return (
          <span style={style}>
            {row.value != null ? formatter(row.value, formatKey) : '\u2014'}
          </span>
        )
      }
    }
  ]
}

function ActivitySummaryRenderer ({ config: rawConfig }) {
  const merged = useMemo(() => mergeConfig(rawConfig), [rawConfig])

  const {
    effectiveConfig,
    quickDate: runtimeQuickDate,
    customDateRange,
    effectiveRows,
    onQuickDateChange,
    onCustomDateRangeChange,
    onRowsChange,
    onRestoreDefaults
  } = useActivitySummaryState(merged)

  const { rows, isFetching, config: resolvedConfig, clientId } = useActivitySummary(effectiveConfig)
  const [drillRow, setDrillRow] = useState(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { formatter } = useFormattingContext()

  const variant = useStyleVariant('Presentation Table', {
    styleVariant: merged.tableStyleVariant,
    styleVariantParams: merged.tableStyleVariantParams
  })

  const handleSettingsApply = useCallback(({ quickDate: qd, customDateRange: cr, rows: r }) => {
    onQuickDateChange(qd)
    if (qd === 'Custom' && cr) onCustomDateRangeChange(cr)
    onRowsChange(r)
  }, [onQuickDateChange, onCustomDateRangeChange, onRowsChange])

  const handleDrillClick = useCallback((row) => {
    if (merged.showDrillDown) {
      setDrillRow(row)
    }
  }, [merged.showDrillDown])

  const quickDateLabel = runtimeQuickDate === 'Custom'
    ? 'Custom'
    : (QUICK_DATE_OPTIONS.find(o => o.value === runtimeQuickDate)?.label ?? runtimeQuickDate)

  const formats = useMemo(() => ({
    value: merged.valueFormat || 'marketValue',
    performance: merged.performanceFormat || 'returns'
  }), [merged.valueFormat, merged.performanceFormat])

  const columns = useMemo(
    () => buildColumns(formatter, merged.showDrillDown ? handleDrillClick : null, quickDateLabel, formats),
    [formatter, merged.showDrillDown, handleDrillClick, quickDateLabel, formats]
  )

  return (
    <FadeIn>
      {!variant.props?.hideTitle && (
        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
          <SmartComponentDisplayName title={merged.title}>
            {merged.showDateControls !== false ? (
              <Tooltip title='Display Settings'>
                <IconButton size='small' onClick={() => setSettingsOpen(true)}>
                  <Icon name='settings' customSize='20px' />
                </IconButton>
              </Tooltip>
            ) : null}
          </SmartComponentDisplayName>
        </div>
      )}
      <OpalPresentationTable
        {...variant.props}
        className={variant.tableClassName}
        style={variant.styles}
        data={rows}
        columns={columns}
        isLoading={isFetching}
        emptyStateText='No activity data available'
      />
      {merged.showDateControls !== false && (
        <ActivitySummarySettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          config={merged}
          quickDate={runtimeQuickDate}
          customDateRange={customDateRange}
          rows={effectiveRows}
          onApply={handleSettingsApply}
          onRestoreDefaults={onRestoreDefaults}
        />
      )}
      {merged.showDrillDown && (
        <DrillDownDialog
          open={!!drillRow}
          onClose={() => setDrillRow(null)}
          row={drillRow}
          config={resolvedConfig}
          clientId={clientId}
        />
      )}
    </FadeIn>
  )
}

ActivitySummaryRenderer.propTypes = {
  config: PropTypes.object
}

export default ActivitySummaryRenderer
